import type { PickupLocation } from "../transporter/transporter";

/**
 * CRM record for a FedEx (or similar) terminal / station.
 *
 * Stored in the top-level Firestore collection `terminals`. The physical
 * address lives in `pickup_locations` and is referenced by
 * {@link Terminal.pickup_location_id} — the same pattern as
 * `ContactCompany.businessAddressId`.
 */
export interface Terminal {
  id: string;
  /**
   * `pickup_locations` doc id holding this terminal's address. Required in
   * practice (the form enforces address, city, state, zip) but nullable so a
   * doc is never unreadable if the location is deleted out from under it.
   */
  pickup_location_id: string | null;
  /** Display name, e.g. "Memphis Hub". */
  name?: string;
  /** Short code, e.g. "MEM1". */
  abbreviation?: string;
  /** Station / terminal number (kept as string to preserve leading zeros). */
  number?: string;
  /** Free-form internal notes for this terminal (optional). */
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
  createdByUserId: string;
}

/** Normalize a stored list of terminal ids (dedupe, trim, drop empties). */
export function normalizeTerminalIds(ids: unknown): string[] {
  const out: string[] = [];
  if (!Array.isArray(ids)) return out;
  for (const entry of ids) {
    if (typeof entry !== "string") continue;
    const trimmed = entry.trim();
    if (!trimmed || out.includes(trimmed)) continue;
    out.push(trimmed);
  }
  return out;
}

/**
 * Human-readable label for a terminal.
 * Prefer "ABBREV #NUMBER — Name", falling back to the linked address.
 */
export function getTerminalDisplayName(
  terminal: Terminal,
  location?: PickupLocation | null,
): string {
  const abbrev = terminal.abbreviation?.trim() ?? "";
  const number = terminal.number?.trim() ?? "";
  const name = terminal.name?.trim() ?? "";

  const identityParts: string[] = [];
  if (abbrev && number) identityParts.push(`${abbrev} #${number}`);
  else if (abbrev) identityParts.push(abbrev);
  else if (number) identityParts.push(`#${number}`);

  if (identityParts.length > 0 && name) {
    return `${identityParts.join(" ")} — ${name}`;
  }
  if (identityParts.length > 0) return identityParts.join(" ");
  if (name) return name;

  if (location) {
    const line1 = location.address?.trim() ?? "";
    const cityState = [location.city, location.state]
      .map((p) => p?.trim())
      .filter(Boolean)
      .join(", ");
    const tail = [cityState, location.zip?.trim()].filter(Boolean).join(" ");
    const address = [line1, tail].filter(Boolean).join(", ");
    if (address) return address;
  }

  return "Unnamed terminal";
}

/**
 * Lowercased haystack of terminal identity + linked address fields for
 * partial client-side search.
 */
export function buildTerminalSearchText(
  terminal: Terminal,
  location?: PickupLocation | null,
): string {
  const parts = [
    terminal.name,
    terminal.abbreviation,
    terminal.number,
    terminal.notes,
    location?.address,
    location?.city,
    location?.state,
    location?.zip,
    location?.company_name,
  ]
    .map((p) => (typeof p === "string" ? p.trim().toLowerCase() : ""))
    .filter(Boolean);
  return parts.join(" ");
}
